import {
  Box,
  Flex,
  Heading,
  List,
  ListItem,
  Text,
  Spinner,
  Badge,
  VStack,
  HStack,
  Divider,
  Textarea,
  Button,
  useToast,
  Alert,
  AlertIcon,
  useDisclosure,
} from "@chakra-ui/react";
import { Message, Session, User, ToolCall } from "core";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFirestore } from "reactfire";
import { httpsCallable } from "firebase/functions";
import { useFunctions } from "reactfire";
import ToolCallModal from "../../../components/ToolCallModal";

type SessionWithId = Session & { id: string };
type MessageWithId = Message & { id: string; isAdminMessage?: boolean };

export default function UserDetail() {
  const { id: communityId, userId } = useParams();
  const firestore = useFirestore();

  const [user, setUser] = useState<User | null>(null);
  const [sessions, setSessions] = useState<SessionWithId[]>([]);
  const [selectedSession, setSelectedSession] = useState<SessionWithId | null>(null);
  const [messages, setMessages] = useState<MessageWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [adminMessage, setAdminMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedToolCall, setSelectedToolCall] = useState<ToolCall | null>(null);
  const toast = useToast();
  const functions = useFunctions();
  const {
    isOpen: isToolCallModalOpen,
    onOpen: onToolCallModalOpen,
    onClose: onToolCallModalClose,
  } = useDisclosure();

  // Fetch user and their sessions
  useEffect(() => {
    async function fetchUserAndSessions() {
      if (!userId || !communityId) return;

      try {
        setLoading(true);

        // Fetch user by document ID
        const userDoc = await getDoc(doc(firestore, "users", userId));
        
        if (userDoc.exists()) {
          setUser(userDoc.data() as User);
        }

        // Fetch sessions for this user
        const sessionsQuery = query(
          collection(firestore, "users", userId, "sessions"),
          where("communityId", "==", communityId),
          orderBy("createdAt", "desc")
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);

        const fetchedSessions = sessionsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as SessionWithId[];

        setSessions(fetchedSessions);

        // Auto-select the first session
        if (fetchedSessions.length > 0) {
          setSelectedSession(fetchedSessions[0]);
        }
      } catch (error) {
        console.error("Error fetching user and sessions:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchUserAndSessions();
  }, [userId, communityId, firestore]);

  // Fetch messages when a session is selected
  useEffect(() => {
    async function fetchMessages() {
      if (!userId || !selectedSession) return;

      try {
        setMessagesLoading(true);

        const messagesQuery = query(
          collection(
            firestore,
            "users",
            userId,
            "sessions",
            selectedSession.id,
            "messages"
          ),
          orderBy("createdAt", "asc")
        );
        const messagesSnapshot = await getDocs(messagesQuery);

        const fetchedMessages = messagesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as MessageWithId[];

        setMessages(fetchedMessages);
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setMessagesLoading(false);
      }
    }

    fetchMessages();
  }, [selectedSession, userId, firestore]);

  const sendAdminMessage = async () => {
    if (!adminMessage.trim() || !userId || !communityId) return;

    try {
      setSendingMessage(true);
      const sendAdminMessageFn = httpsCallable(functions, "sendAdminMessage");
      
      await sendAdminMessageFn({
        userId,
        message: adminMessage.trim(),
        communityId,
      });

      toast({
        title: "Message sent successfully",
        description: "The WhatsApp message has been sent to the user.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setAdminMessage("");
      
      // Refresh messages to show the new admin message
      if (selectedSession) {
        const messagesQuery = query(
          collection(
            firestore,
            "users",
            userId,
            "sessions",
            selectedSession.id,
            "messages"
          ),
          orderBy("createdAt", "asc")
        );
        const messagesSnapshot = await getDocs(messagesQuery);
        const fetchedMessages = messagesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as MessageWithId[];
        setMessages(fetchedMessages);
      }
    } catch (error) {
      console.error("Error sending admin message:", error);
      toast({
        title: "Failed to send message",
        description: error instanceof Error ? error.message : "An error occurred while sending the message.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        Loading...
      </Box>
    );
  }

  if (!user) {
    return <Box>User not found</Box>;
  }

  return (
    <Box>
      <Box fontSize="xl" fontWeight="bold" mb={4}>
        {user.name}
      </Box>
      <Text mb={6} color="gray.600">
        {user.phoneNumber || "No phone number"}
      </Text>

      <Flex gap={4} height="70vh">
        {/* Left Pane: Sessions List */}
        <Box
          width="30%"
          borderWidth={1}
          borderRadius="md"
          p={4}
          overflowY="auto"
          bg="gray.50"
        >
          <Heading size="md" mb={4}>
            Sessions ({sessions.length})
          </Heading>
          {sessions.length === 0 ? (
            <Text color="gray.500">No sessions found</Text>
          ) : (
            <List spacing={2}>
              {sessions.map((session) => {
                const isActive = selectedSession?.id === session.id;
                const isExpired = session.expiresAt
                  ? (session.expiresAt as Timestamp).toDate() < new Date()
                  : false;
                const isAnalyzed = !!session.analyzedAt;

                return (
                  <ListItem
                    key={session.id}
                    p={3}
                    borderRadius="md"
                    cursor="pointer"
                    bg={isActive ? "blue.100" : "white"}
                    borderWidth={1}
                    borderColor={isActive ? "blue.500" : "gray.200"}
                    _hover={{ bg: isActive ? "blue.100" : "gray.100" }}
                    onClick={() => setSelectedSession(session)}
                  >
                    <VStack align="start" spacing={1}>
                      <HStack>
                        <Text fontSize="sm" fontWeight="semibold">
                          {session.id.substring(0, 8)}...
                        </Text>
                        {isExpired && (
                          <Badge colorScheme={isAnalyzed ? "green" : "yellow"}>
                            {isAnalyzed ? "Analyzed" : "Pending"}
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.600">
                        Created:{" "}
                        {(session.createdAt as Timestamp)
                          ?.toDate()
                          .toLocaleDateString()}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        Expires:{" "}
                        {(session.expiresAt as Timestamp)
                          ?.toDate()
                          .toLocaleDateString()}
                      </Text>
                    </VStack>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        {/* Right Pane: Messages */}
        <Box
          width="70%"
          borderWidth={1}
          borderRadius="md"
          p={4}
          overflowY="auto"
          bg="white"
        >
          {selectedSession ? (
            <>
              <Heading size="md" mb={4}>
                Messages
              </Heading>
              {messagesLoading ? (
                <Flex justify="center" align="center" height="100%">
                  <Spinner />
                </Flex>
              ) : messages.length === 0 ? (
                <Text color="gray.500">No messages in this session</Text>
              ) : (
                <VStack align="stretch" spacing={3}>
                  {messages.map((message) => {
                    const isUser = message.role === "user";
                    const hasToolCall = 
                      (message.role === "assistant" || message.role === "tool") && 
                      "toolCall" in message && 
                      message.toolCall;
                    
                    return (
                      <Box
                        key={message.id}
                        p={3}
                        borderRadius="md"
                        bg={isUser ? "blue.50" : "gray.50"}
                        borderLeftWidth={4}
                        borderLeftColor={isUser ? "blue.500" : "gray.500"}
                      >
                        <HStack justify="space-between" mb={2}>
                          <HStack>
                            <Badge colorScheme={isUser ? "blue" : "gray"}>
                              {message.role}
                            </Badge>
                            {message.isAdminMessage && (
                              <Badge colorScheme="purple" size="sm">
                                Admin
                              </Badge>
                            )}
                            {hasToolCall && "toolCall" in message && message.toolCall && (
                              <Badge
                                colorScheme="orange"
                                size="sm"
                                cursor="pointer"
                                onClick={() => {
                                  if ("toolCall" in message && message.toolCall) {
                                    setSelectedToolCall(message.toolCall);
                                    onToolCallModalOpen();
                                  }
                                }}
                                _hover={{ transform: "scale(1.05)" }}
                                transition="transform 0.1s"
                              >
                                🔧 {message.toolCall.name}
                              </Badge>
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            {(message.createdAt as Timestamp)
                              ?.toDate()
                              .toLocaleString()}
                          </Text>
                        </HStack>
                        <Text whiteSpace="pre-wrap">{message.message}</Text>
                      </Box>
                    );
                  })}
                </VStack>
              )}
              
              {/* Admin Message Input */}
              <Divider my={4} />
              <Box>
                <Heading size="sm" mb={3} color="purple.600">
                  Send Admin Message
                </Heading>
                {!user.phoneNumber ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">
                      User has no phone number. Cannot send WhatsApp message.
                    </Text>
                  </Alert>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    <Textarea
                      placeholder="Type your message here..."
                      value={adminMessage}
                      onChange={(e) => setAdminMessage(e.target.value)}
                      rows={3}
                      resize="vertical"
                    />
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.500">
                        This message will be sent via WhatsApp to {user.phoneNumber}
                      </Text>
                      <Button
                        colorScheme="purple"
                        size="sm"
                        onClick={sendAdminMessage}
                        isLoading={sendingMessage}
                        loadingText="Sending..."
                        isDisabled={!adminMessage.trim()}
                      >
                        Send Message
                      </Button>
                    </HStack>
                  </VStack>
                )}
              </Box>
            </>
          ) : (
            <Flex justify="center" align="center" height="100%">
              <Text color="gray.500">Select a session to view messages</Text>
            </Flex>
          )}
        </Box>
      </Flex>

      {/* Tool Call Modal */}
      {selectedToolCall && (
        <ToolCallModal
          isOpen={isToolCallModalOpen}
          onClose={onToolCallModalClose}
          toolCall={selectedToolCall}
        />
      )}
    </Box>
  );
}
